import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useMediaQuery } from "@mui/material";

import { HomeStyles } from "./HomeStyles";
import { useTheme } from "../../providers/ThemeContext";
import { useI18n } from "../../languages/providers/I18nProvider";
import { GlobalStyles } from "../../styles/GlobalStyles";
import WhatsAppFab from "../../components/common/WhatsAppFab";
import CategoryCard from "../../components/cards/categoryCard/CategoryCard";
import { homeKeys, categoryImageAlts } from "./HomeContent";
import Header from "../../components/website/header/Header";
import HeroContent from "./HeroContent";
import { useLangPath } from "../../hooks/useLangPath";

import bg from "../../assets/logo/uae-flag-aerial-cityscape-flagpole.jpg";

import TrustedBy from "../../components/common/TrustedBy";
import AnnouncementBar from "../../components/common/AnnouncementBar";
import SEO from "../../components/common/SEO";
import FaqAccordion from "../../components/common/FaqAccordion";
import EidHero from "../../components/EidHero/EidHero";
import { useEid55Active } from "../../config/campaigns";

const VISUALLY_HIDDEN: React.CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  margin: -1,
  padding: 0,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
};

const HomePage = () => {
  const { t, lang } = useI18n();
  const { theme } = useTheme();
  const lp = useLangPath();

  const globalStyles = GlobalStyles(theme);

  const isMobile = useMediaQuery("(max-width: 768px)");
  const localStyles = HomeStyles(theme, isMobile);

  const { hero, sections, features, categories, process, faq } = homeKeys;

  const videoRef = useRef<HTMLVideoElement>(null);
  const eidActive = useEid55Active();

  const ANIM_CSS = `
@keyframes fadeInOut {
  0%   { opacity: 0; transform: translate(-50%, 12px); }
  10%  { opacity: 1; transform: translate(-50%, 0); }
  60%  { opacity: 1; transform: translate(-50%, 0); }
  90%  { opacity: 0; transform: translate(-50%, -6px); }
  100% { opacity: 0; transform: translate(-50%, -6px); }
}
`;

  useEffect(() => {
    const reduce = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    )?.matches;
    if (reduce) videoRef.current?.pause();
  }, []);

  return (
    <>
      <AnnouncementBar />
      <SEO
        title={t("seo:home:title")}
        description={t("seo:home:description")}
        lang={lang}
        schema={[
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faq.items.map((item) => ({
              "@type": "Question",
              name: t(item.q),
              acceptedAnswer: { "@type": "Answer", text: t(item.a) },
            })),
          },
        ]}
      />
      <style dangerouslySetInnerHTML={{ __html: ANIM_CSS }} />
      {eidActive ? (
        <>
          <Header variant="eid55" />
          {/* Page heading for the business; the Eid slogan below is styled copy.
              Visually hidden so the campaign design is unchanged. */}
          <h1 style={VISUALLY_HIDDEN}>{t(hero.seoH1)}</h1>
          <EidHero
            whatsappUrl={`https://wa.me/971554071164?text=${encodeURIComponent(t("contact:messages:whatsapp"))}`}
            shopPath="/ourwork/eid-al-etihad-55"
          />
        </>
      ) : (
        <div style={{ position: "relative", isolation: "isolate" as const }}>
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 0,
              pointerEvents: "none",
            }}
          >
            <img
              src={bg}
              alt=""
              style={localStyles.heroImageBg}
            />
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "linear-gradient(rgba(0,0,0,.28), rgba(0,0,0,.22))",
              }}
            />
          </div>

          <div style={{ position: "relative", zIndex: 1 }}>
            <Header transparentBg />
            <div style={localStyles.animatedTagline}>
              <span style={localStyles.animatedText}>
                {t(homeKeys.hero.subtitle)}
              </span>
            </div>
            <div style={{ ...localStyles.page, background: "transparent" }}>
              <section style={localStyles.hero}>
                <div
                  style={{
                    ...localStyles.heroInner,
                    ...(isMobile ? localStyles.heroInnerSm : {}),
                  }}
                >
                  <HeroContent keys={hero} />
                </div>
              </section>
            </div>
          </div>
        </div>
      )}
      {/* ====== BELOW: normal background starts before Stats ====== */}

      <TrustedBy
        title={t("home:hero:trustedBy")}
        logos={homeKeys.trustedBy.logos}
        grayscale
      />
      <hr
        style={{
          border: "none",
          borderTop: `1px solid ${theme.border ?? "rgba(0,0,0,0.08)"}`,
          margin: "0 0 64px 0",
        }}
      />
      <main style={localStyles.page}>
        {/* ===== Popular categories ===== */}
        <section
          style={{
            ...globalStyles.section,
            ...(isMobile ? localStyles.sectionSm : {}),
          }}
        >
          <div style={{ marginBottom: 45 }}>
            <span
              style={{
                display: "block",
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.12em",
                textTransform: "uppercase" as const,
                color: "#c9a96e",
                borderBottom: "none",
                paddingBottom: 0,
                marginBottom: 8,
              }}
            >
              — {t("home:sections:popularEyebrow")}
            </span>
            <h2
              style={{
                ...localStyles.sectionCategoriesTitle,
                display: "block",
              }}
            >
              {t(sections.popularTitle)}
            </h2>
          </div>

          <div style={{ display: "flex", flexDirection: "column" }}>
            {categories.map((c, i) => (
              <CategoryCard
                key={c.key}
                index={i}
                title={t(c.key)}
                subtitle={t(c.subtitle)}
                desc={c.descKey ? t(c.descKey) : undefined}
                img={c.image}
                alt={categoryImageAlts[c.key] ?? t(c.key)}
                to={lp(c.to)}
                reverse={i % 2 === 1}
                ctaLabel={t("common:learnMore")}
              />
            ))}
          </div>
        </section>
        {/* ===== STATS ===== */}
        {/* <div
          style={{
            ...s.statsStrip,
            borderTop: `1px solid ${theme.border}`,
            paddingTop: 10,

            paddingBottom: 0,
            margin: `40px auto`,
            maxWidth: "auto",
          }}
        >
          {homeKeys.stats.map((st) => (
            <div key={st.label} style={s.statCard}>
              <div style={s.statValue}>{st.value}</div>
              <div style={s.statLabel}>{t(st.label)}</div>
            </div>
          ))}
        </div> */}
        {/* ===== How we work ===== */}
        <section
          style={{
            ...globalStyles.section,
            ...(isMobile ? localStyles.sectionSm : {}),
          }}
        >
          <h2 style={localStyles.sectionTitle}>{t(sections.howTitle)}</h2>
          <div
            style={{
              ...localStyles.processStrip,
              ...(isMobile
                ? {
                    gridTemplateColumns: "1fr",
                    borderTop: "none",
                  }
                : {}),
            }}
          >
            {process.map((p, i) => (
              <div
                key={p}
                style={{
                  ...localStyles.processStep,
                  ...(isMobile
                    ? {
                        borderInlineEnd: "none",
                        borderBottom: `1px solid ${theme.border ?? "rgba(0,0,0,0.07)"}`,
                        paddingInlineStart: 0,
                      }
                    : {}),
                  // No border on the inline-end edge of the last item
                  ...(i === process.length - 1
                    ? { borderInlineEnd: "none" }
                    : {}),
                }}
              >
                {/* Step number */}
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.1em",
                    color: theme.primary,
                    marginBottom: 10,
                    fontFamily: "'Poppins', sans-serif",
                  }}
                >
                  {String(i + 1).padStart(2, "0")}
                </div>

                {/* Step text */}
                <div style={{ fontSize: 14, lineHeight: 1.7 }}>{t(p)}</div>
              </div>
            ))}
          </div>
        </section>
        {/* ===== Why our flags ===== */}
        <section
          style={{
            ...globalStyles.section,
            ...(isMobile ? localStyles.sectionSm : {}),
            borderTop: `1px solid ${theme.border}`,
          }}
        >
          <h2 style={localStyles.sectionTitle}>{t(sections.whyTitle)}</h2>

          <div
            style={{
              ...localStyles.featuresList,
              ...(isMobile ? localStyles.featuresListSm : {}),
              marginBottom: 40,
            }}
          >
            {features.map((f) => (
              <div key={f.title} style={localStyles.featureItem}>
                <span style={localStyles.featureDot} />
                <div>
                  <div style={globalStyles.featureTitle}>{t(f.title)}</div>
                  <div style={globalStyles.featureText}>{t(f.text)}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ===== FAQ ===== */}
        <section
          style={{
            ...globalStyles.section,
            ...(isMobile ? localStyles.sectionSm : {}),
          }}
        >
          <h2 style={localStyles.sectionTitle}>{t(faq.title)}</h2>
          <FaqAccordion
            items={faq.items.map((item) => ({
              q: t(item.q),
              a: t(item.a),
            }))}
            defaultOpen={0}
          />
        </section>

        {/* ===== Final CTA ===== */}
        <section
          style={{
            ...globalStyles.section,
            ...(isMobile ? localStyles.sectionSm : {}),
            borderTop: `1px solid ${theme.border}`,
          }}
        >
          <div
            style={{
              ...localStyles.finalCTA,
              ...(isMobile ? localStyles.finalCtaSm : {}),
            }}
          >
            <div style={localStyles.finalText}>{t(sections.finalLead)}</div>
            <Link to={lp("/contact")} style={globalStyles.btnPrimary}>
              {t(sections.finalBtn)}
            </Link>
          </div>
        </section>
        <WhatsAppFab
          phone="971554071164"
          message={t("contact:messages:whatsapp")}
          ariaLabel={t("common:chatWhatsApp")}
          tooltipText={t("contact:messages:whatsappTooltip")}
          bottom={22}
          left={22}
        />
      </main>
    </>
  );
};

export default HomePage;
